import React, { useState, useEffect, useMemo, useCallback } from "react";
import t from "../../locales/translations";
import siteURL from "../../utils/siteURL";
import {
  isUserVerified,
  setVerifiedValue,
  isResendCodeActive,
  setResendCodeValue,
} from "../../utils/localStorage";
import {
  cognitoConfirmSignUp,
  cognitoResendConfirmationCode,
} from "../../utils/cognitoService";
import { isMobileDevice } from "../../utils/device";

export default function ConfirmUser() {
  const [result, setResult] = useState(0); // 0: initial, 1: success, 2: expired code, 3: generic error
  const queryString = window.location.search;
  const urlParams = useMemo(
    () => new URLSearchParams(queryString),
    [queryString]
  );
  const {
    debug = "",
    // email = "",
    signUpOrigin,
    confirmation_code = "blank",
    user_name = "blank", // this is set to the customer's Cognito sub
  } = useMemo(() => Object.fromEntries(urlParams), [urlParams]);

  const origin = signUpOrigin === "shopify" ? "shopify" : "mobile";
  const clientId =
    origin === "shopify"
      ? process.env.REACT_APP_SHOPIFY_COGNITO_CLIENT_ID
      : process.env.REACT_APP_MOBILE_COGNITO_CLIENT_ID;

  if (debug) {
    console.debug(
      "debug:",
      JSON.stringify({ debug, result, origin, mobileDevice: isMobileDevice() })
    );
  }

  document.title = t("title");

  const confirmSignUp = useCallback(async () => {
    // Check if user is already verified
    if (isUserVerified(user_name)) {
      setResult(1); // User already verified
      return; // no need to confirm again
    }

    try {
      console.log("Confirming sign-up for user:", user_name);
      if (debug.indexOf("success") !== -1) {
        console.debug("bypass confirmSignUpCommand");
      } else if (debug.indexOf("expired") !== -1) {
        const error = new Error("Simulated ExpiredCodeException");
        error.name = "ExpiredCodeException";
        throw error;
      } else {
        await cognitoConfirmSignUp(clientId, confirmation_code, user_name);
      }
      setResult(1);

      // Store the verified user in localStorage
      setVerifiedValue(user_name, new Date().getTime());
    } catch (error) {
      switch (error.name) {
        case "CodeMismatchException":
        case "ExpiredCodeException":
          console.error("Expired confirmation code for user:", user_name);
          setResult(2); // Expired code
          break;
        case "NotAuthorizedException":
          if (error.message.includes("is CONFIRMED")) {
            console.warn("User already confirmed:", user_name);
            setResult(1); // User already confirmed
          }
          break;
        default:
          console.error("Error confirming sign-up:", error);
          setResult(3); // Other error
      }
    }
  }, [clientId, confirmation_code, user_name, debug]);

  const resendCode = useCallback(async () => {
    // Check if user is already verified
    if (isResendCodeActive(user_name)) {
      console.log("Resend code already sent for user:", user_name);
      return; // no need to resend code again
    }

    try {
      if (debug.indexOf("success") !== -1) {
        console.debug("bypass resendCodeCommand");
        return; // no need to resend code again
      } else {
        await cognitoResendConfirmationCode(clientId, user_name);
      }
      setResendCodeValue(user_name, new Date().getTime());
    } catch (error) {
      setResult(3); // Other error
      console.error("Error resending confirmation code:", error);
    }
  }, [clientId, user_name, debug]);

  const handleResult = useCallback(
    (result) => {
      if (result === 0) {
        confirmSignUp();
      }
      if (result === 1) {
        if (origin === "mobile" && isMobileDevice()) {
          setTimeout(() => {
            switch (process.env.REACT_APP_ENVIRONMENT) {
              case "dev":
                window.location = `https://apps-dev.therabody.com/`;
                break;
              case "stage":
                window.location = `https://apps-stage.therabody.com/`;
                break;
              default:
                // window.location = `therabody://auth/login/${email}`;
                window.location = "https://apps.therabody.com/";
            }
          }, 2000);
        }
      }
      if (result !== 0) {
        // Clean the URL
        window.history.pushState("Therabody", "Therabody", "/account");
      }
    },
    [confirmSignUp, origin]
  );

  useEffect(() => {
    handleResult(result);
  }, [result, handleResult]);

  const renderMessage = (headerKey, bodyKey) => (
    <div className="confirm-container">
      <div className="confirm-image-container">
        <img
          align="center"
          alt=""
          src="https://g4app.s3-us-west-2.amazonaws.com/img/therabody.png"
          width="200"
        />
      </div>
      <div className="confirm-header">{headerKey ? t(headerKey) : ""}</div>
      <div className="confirm-body">
        <p>{bodyKey ? t(bodyKey) : ""}</p>
        {result === 2 && (
          <p>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (isResendCodeActive(user_name)) {
                  e.target.parentElement.innerHTML = t("emailSent");
                } else {
                  e.target.parentElement.innerHTML = t("emailResent");
                }
                resendCode();
              }}
            >
              {t("resendEmail")}
            </button>
          </p>
        )}
        {origin === "shopify" && result === 1 ? ( // Only show link on success for Shopify
          <p>
            <a href={siteURL()}>{siteURL()}</a>
          </p>
        ) : (
          ""
        )}
      </div>
    </div>
  );

  return (
    <div>
      {result === 1 && renderMessage("thanks", `${origin}Close`)}
      {result === 2 && renderMessage("expired", "checkEmail")}
      {result === 3 && renderMessage("error", null)}
    </div>
  );
}
